/**
 * Doing things with your hands in a session.
 *
 * Everything the player does in the world goes through one path on a flat
 * screen: `survival/interaction.ts` keeps the nearest interactable the player
 * is *looking at* — a dot product against the camera's forward, plus a
 * line-of-sight check — and `E` takes it. Neither half of that survives a
 * headset intact. There is no camera forward worth aiming with when a hand is
 * holding the ray, and there is no `E`.
 *
 * So in a session the same act is expressed as a ray plus a press, and this
 * file is where the two meet. It consumes `XRIntent` (see `xrintent.ts`) and
 * nothing else — no `XRInputSource`, no gamepad, no handedness — which is what
 * lets one design serve a Quest and an Apple Vision Pro at once.
 *
 * **The Vision Pro constraint is the whole design.** Its WebXR input is
 * `transient-pointer`: the ray exists only at the instant of a pinch, pointing
 * where the player is looking, and Apple deliberately exposes no continuous
 * gaze. There is therefore no hover, no cursor to poll, nothing to preview.
 * The first frame of a pinch has to both *select* and *commit*, or the player
 * pinches at an almond water and nothing happens.
 *
 * That falls out of one rule: **the selection is computed from this frame's
 * ray, and a press commits against it in the same frame.** On a controller the
 * ray is continuous, so the selection is already on screen when the trigger
 * goes down and the player gets a hover preview for free; on Vision Pro the
 * selection is computed and consumed within the single frame the ray exists.
 * The code does not branch on which device it is talking to — the hover is
 * simply what a continuous ray looks like.
 *
 * Reach is longer than the flat game's `TUNING.player.interactRange`, and has
 * to be: 2.4 m is an arm plus a step, which is the right distance when walking
 * is free, and the wrong one in rooms three metres to a cell where crossing the
 * floor costs a teleport hop. See `TUNING.render.xr.interactReach`, which is
 * where every number this file aims with lives.
 *
 * What this file deliberately does not know: what any of these things *are*.
 * It is handed points with radii, it says which one the ray found and when the
 * player fired at it, and `game.ts` — the only file allowed to know that id 7 is
 * a granola bar and id 100002 is a hole in the floor — does the rest. It does
 * not even know what any of them are *called*: the words belong to the prompt,
 * the prompt belongs to `survival/`, and `SurvivalSystem.setFocusTo` is where
 * this file's answer is handed back to the system that owns it.
 *
 * Nothing in here runs outside a session. `attach()` is called on
 * `sessionstart` and is the first moment any geometry exists; before that, and
 * on every desktop frame this game will ever run, the object holds three
 * booleans and an empty pool.
 */

import * as THREE from 'three';
import { TUNING } from '../core/config';
import { hasLineOfSight } from '../core/grid';
import { clamp, damp } from '../core/math';
import type { Grid, Vec2 } from '../core/types';
import type { XRIntent, XRIntentSet } from './xrintent';

/**
 * Radians of angular slop added to a target's own radius, per metre of range.
 *
 * A fixed radius alone is unusable at distance — a 0.3 m bottle six metres away
 * subtends 2.9°, and neither a hand-held ray nor a gaze ray is that steady.
 * This keeps the *apparent* size of the target roughly constant instead. 0.05
 * is a little under 3° of half-angle, about the width of a thumbnail held at
 * arm's length.
 *
 * In `TUNING` with the reaches and the aim radii, because every one of them was
 * reasoned from geometry rather than felt in a headset and they will all want
 * moving together once somebody has worn one.
 */
const ANGLE_SLOP = TUNING.render.xr.aimAngleSlop;

/** Metres in front of the origin before a target counts as pointed at. */
const MIN_ALONG = 0.08;

/**
 * Score given up per metre of range, so that two things under one ray resolve
 * to the nearer. The same idea as `DISTANCE_BIAS` in `survival/interaction.ts`,
 * scaled for the longer reach.
 */
const DISTANCE_BIAS = 0.04;

/**
 * Score handed to whatever was already selected.
 *
 * Two supplies a hand's width apart at range are separated by less than the
 * jitter in the ray, and a selection that flickers between them is one the
 * player cannot commit to on purpose. Small enough that deliberately moving the
 * ray still changes the answer immediately.
 */
const STICKY = 0.06;

/**
 * Preference for the hand that is actually pressing.
 *
 * With two controllers both rays produce a selection, and only one of them is
 * about to be fired. The highlight has to be on that one — otherwise the player
 * watches a ring on the thing they are *not* taking.
 */
const ACTING_BONUS = 0.25;

/* -- The highlight. -------------------------------------------------- */

/** Radians of half-angle the ring tries to subtend, and its metre limits. */
const RING_ANGLE = 0.075;
const RING_MIN = 0.16;
const RING_MAX = 0.6;
/** How fast the ring fades in and out, and how long a commit flash lasts. */
const RING_FADE_RATE = 14;
const FLASH_DECAY = 0.26;
/** Warm off-white, the same family as the strike ring in `remotestrike.ts`. */
const RING_COLOUR = 0xffe2bc;
const RING_FLASH_COLOUR = 0xfff2e0;

/** What sort of thing the ray found. The only distinction this file draws. */
export type XRTargetKind =
  /** A supply, a note, a way down: something a press takes. */
  | 'thing'
  /** Another player: something a *hold* winds up on. See `remotestrike.ts`. */
  | 'person';

/**
 * One thing the ray can be pointed at, as this file needs it.
 *
 * Instances are **pooled and reused**: `add()` hands back a record from a list
 * that lives for the session, the caller fills every field in, and it is
 * invalid the moment `end()` returns. Nothing may retain one. That is what
 * keeps a per-frame candidate list free of garbage in a headset, where a
 * collection pause is a visible judder in both eyes.
 */
export interface XRTarget {
  /** Whatever the caller uses to recognise it again. Never interpreted here. */
  id: number;
  kind: XRTargetKind;
  /** World-space point the ray must pass near, metres. */
  x: number;
  y: number;
  z: number;
  /** Metres of slop around that point, before `ANGLE_SLOP` is added. */
  radius: number;
  /** Metres past which this thing is out of reach. */
  reach: number;
}

/** What this file needs from the game that owns it. */
export interface XRInteractHost {
  /** The scene the highlight is parented to, for the life of a session. */
  readonly scene: THREE.Scene;
}

/**
 * The ray's interaction, as `game.ts` drives it.
 *
 * One frame is `begin()`, zero or more `add()`s, `end()`. `begin()` returning
 * false means there is nothing worth offering candidates for — no session, or
 * no intents, or the player may not act — and `end()` must still be called, so
 * that a selection left over from the previous frame is taken down.
 */
export interface XRInteract {
  /** True between `attach()` and `detach()`; false for the life of a desktop. */
  readonly attached: boolean;
  /** Id of what the ray is on, or -1. */
  readonly selected: number;
  readonly selectedKind: XRTargetKind;
  /**
   * Id the player committed on this frame, or -1.
   *
   * Only ever a `thing`: a person is not something a press does anything to.
   * Set on the frame the primary action *starts*, against that same frame's
   * ray, which is the only arrangement a transient pointer can satisfy.
   */
  readonly fired: number;
  /** True while the primary action is held with a person under the ray. */
  readonly striking: boolean;
  /**
   * True when the press that started this frame belongs to something in the
   * world rather than to the floor.
   *
   * Locomotion and interaction share one button on both headsets — there is no
   * second one on Vision Pro — so the target has to decide which of the two a
   * press meant. Nothing reads this yet; see the request for
   * `XRPresenter.setActionClaim` in the batch notes.
   */
  readonly claimed: boolean;

  /** A session started. Builds the highlight the first time it is called. */
  attach(): void;
  /** A session ended. Takes the highlight out of the scene and forgets state. */
  detach(): void;

  /** Open this frame's candidate list. False when there is nothing to pick. */
  begin(intents: XRIntentSet | null, canAct: boolean): boolean;
  /** A pooled record to fill in. Valid until `end()` returns. */
  add(): XRTarget;
  /**
   * Close the list: pick, draw, and say what is selected. `head` supplies the
   * world-space eye the highlight billboards to; `grid` refuses a target
   * through a wall, exactly as the flat path does.
   */
  end(dt: number, head: THREE.Camera, grid: Grid | null): void;

  /**
   * A camera whose local transform *is* the aiming ray, or null when nothing is
   * being aimed at a person.
   *
   * `remotestrike.ts` aims off a camera's `position` and `quaternion`, which in
   * a session are a head pose. Handing it this instead is what points the
   * wind-up down the ray without that file having to learn what a ray is.
   */
  aim(): THREE.Camera | null;

  dispose(): void;
}

/* -- Scratch. Module-level, so a frame in a headset allocates nothing. -- */
const _origin = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _look = new THREE.Vector3();
const _up = new THREE.Vector3();
const _basis = new THREE.Matrix4();
/** `hasLineOfSight` takes `Vec2`s; these stand in for them. */
const _from: Vec2 = { x: 0, z: 0 };
const _to: Vec2 = { x: 0, z: 0 };

/** A fresh, empty pool entry. */
function emptyTarget(): XRTarget {
  return { id: -1, kind: 'thing', x: 0, y: 0, z: 0, radius: 0, reach: 0 };
}

class Interact implements XRInteract {
  attached = false;
  selected = -1;
  selectedKind: XRTargetKind = 'thing';
  fired = -1;
  striking = false;
  claimed = false;

  /** Reused for the life of the object; `count` is how much of it is live. */
  private readonly pool: XRTarget[] = [];
  private count = 0;

  private intents: XRIntentSet | null = null;
  /** Score of the last `pick`, so the caller can compare across hands. */
  private pickScore = 0;

  /** The ray a strike is being wound up along, when there is one. */
  private aiming = false;
  private readonly aimOrigin = new THREE.Vector3();
  private readonly aimDir = new THREE.Vector3();
  private proxy: THREE.PerspectiveCamera | null = null;

  /**
   * Built on the first session and never on a flat machine — a ring and a
   * material are small, but a desktop that will never wear a headset should not
   * be carrying either. Kept between sessions once built.
   */
  private group: THREE.Group | null = null;
  private ring: THREE.Mesh | null = null;
  private geometry: THREE.RingGeometry | null = null;
  private material: THREE.MeshBasicMaterial | null = null;

  /** Where the ring is, so a commit flash outlives the thing it was on. */
  private markX = 0;
  private markY = 0;
  private markZ = 0;
  private fade = 0;
  private flash = 0;
  private clock = 0;


  constructor(private readonly host: XRInteractHost) {}

  /* ---------------------------------------------------------------- *
   * Session lifecycle
   * ---------------------------------------------------------------- */

  attach(): void {
    if (this.attached) return;
    this.attached = true;
    this.reset();
    this.host.scene.add(this.build());
  }

  detach(): void {
    if (!this.attached) return;
    this.attached = false;
    if (this.group) this.host.scene.remove(this.group);
    this.reset();
  }

  private reset(): void {
    this.count = 0;
    this.intents = null;
    this.selected = -1;
    this.selectedKind = 'thing';
    this.fired = -1;
    this.striking = false;
    this.claimed = false;
    this.aiming = false;
    this.fade = 0;
    this.flash = 0;
  }

  private build(): THREE.Group {
    if (this.group) return this.group;
    const group = new THREE.Group();
    group.name = 'xr-selection';
    // A unit ring, scaled per frame.
    this.geometry = new THREE.RingGeometry(0.87, 1, 32);
    this.material = new THREE.MeshBasicMaterial({
      color: RING_COLOUR,
      transparent: true,
      opacity: 0,
      // Deliberately not depth-tested, unlike the strike ring. A pickup rests
      // on the floor, so a ring centred on one and billboarded at the eye has
      // its lower half inside the floor; depth-testing it would cut the mark in
      // two at exactly the distance the player is trying to read it from.
      // Nothing is given away by that — the selection has already passed a
      // line-of-sight check, so this cannot mark something through a wall.
      depthTest: false,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      fog: false,
    });
    this.ring = new THREE.Mesh(this.geometry, this.material);
    // Above the strike ring's 3: when both are on screen the selection is the
    // one the player is steering with.
    this.ring.renderOrder = 4;
    this.ring.frustumCulled = false;
    group.add(this.ring);
    group.visible = false;
    this.group = group;
    return group;
  }

  /* ---------------------------------------------------------------- *
   * The frame
   * ---------------------------------------------------------------- */

  begin(intents: XRIntentSet | null, canAct: boolean): boolean {
    this.count = 0;
    this.fired = -1;
    this.striking = false;
    this.claimed = false;
    this.aiming = false;
    this.intents = canAct ? intents : null;
    return this.attached && this.intents !== null;
  }

  add(): XRTarget {
    const i = this.count++;
    const held = this.pool[i];
    if (held) return held;
    const fresh = emptyTarget();
    this.pool.push(fresh);
    return fresh;
  }

  end(dt: number, head: THREE.Camera, grid: Grid | null): void {
    if (!this.attached) return;
    this.clock += dt;
    const intents = this.intents;
    this.intents = null;

    let best = -1;
    let bestScore = -Infinity;
    // Kept apart from `bestScore` so that two hands pressing on the same frame
    // resolve by which one is better aimed rather than by which one the runtime
    // happened to list first.
    let firedScore = -Infinity;
    let strikeScore = -Infinity;

    if (intents) {
      // Every hand that is aiming, not just the primary one: with two
      // controllers the player expects the hover to follow whichever hand is
      // pointing at something, and `ACTING_BONUS` settles it the moment one of
      // them presses. A transient pointer contributes exactly one entry, and
      // only on the frames it exists.
      for (const intent of intents.all) {
        const found = this.pick(intent, grid);
        if (found < 0) continue;
        const target = this.pool[found]!;
        const score = this.pickScore + (intent.acting ? ACTING_BONUS : 0);

        // The commit, and the reason the whole file is shaped this way: this is
        // the same frame the ray was read in, so a pointer that exists for one
        // frame is enough to take something with.
        if (intent.actionStarted && target.kind === 'thing' && score > firedScore) {
          firedScore = score;
          this.fired = target.id;
          this.claimed = true;
          this.flash = 1;
          this.markX = target.x;
          this.markY = target.y;
          this.markZ = target.z;
        }
        // A strike is a hold rather than a press — see `remotestrike.ts` — so
        // it is reported for as long as the ray stays on them.
        if (intent.acting && target.kind === 'person' && score > strikeScore) {
          strikeScore = score;
          this.striking = true;
          this.claimed = true;
          this.aiming = true;
          this.aimOrigin.copy(_origin);
          this.aimDir.copy(_dir);
        }

        if (score <= bestScore) continue;
        bestScore = score;
        best = found;
      }
    }

    const target = best >= 0 ? this.pool[best]! : null;
    this.selected = target ? target.id : -1;
    this.selectedKind = target ? target.kind : 'thing';
    this.drawRing(dt, target, head);
  }

  aim(): THREE.Camera | null {
    if (!this.aiming) return null;
    const cam = this.proxy ?? (this.proxy = new THREE.PerspectiveCamera());
    cam.position.copy(this.aimOrigin);
    _look.copy(this.aimOrigin).add(this.aimDir);
    // A ray straight up or straight down has no usable horizontal reference;
    // any perpendicular will do, and the roll about the ray is never read.
    _up.set(0, 1, 0);
    if (Math.abs(this.aimDir.y) > 0.999) _up.set(0, 0, -1);
    // `Matrix4.lookAt` is the camera convention: -Z ends up along the ray,
    // which is what `RemotePlayers.pick` reads a forward out of.
    _basis.lookAt(cam.position, _look, _up);
    cam.quaternion.setFromRotationMatrix(_basis);
    return cam;
  }

  dispose(): void {
    this.detach();
    this.geometry?.dispose();
    this.material?.dispose();
    this.geometry = null;
    this.material = null;
    this.ring = null;
    this.group = null;
    this.proxy = null;
    this.pool.length = 0;
  }

  /* ---------------------------------------------------------------- *
   * Internals
   * ---------------------------------------------------------------- */

  /**
   * The best candidate along one intent's ray, or -1.
   *
   * Leaves the normalised ray in `_origin`/`_dir` and the winner's score in
   * `pickScore`, so the caller can compare hands without either of them being
   * recomputed.
   */
  private pick(intent: XRIntent, grid: Grid | null): number {
    this.pickScore = -Infinity;
    const ray = intent.ray;
    // Null on every frame of a Vision Pro session that is not a pinch, which is
    // most of them. That is the device working as designed, not a failure.
    if (!ray) return -1;
    _origin.copy(ray.origin);
    _dir.copy(ray.direction);
    const length = _dir.length();
    if (length < 1e-6) return -1;
    _dir.multiplyScalar(1 / length);

    let best = -1;
    let bestScore = -Infinity;
    for (let i = 0; i < this.count; i++) {
      const t = this.pool[i]!;
      const dx = t.x - _origin.x;
      const dy = t.y - _origin.y;
      const dz = t.z - _origin.z;
      const along = dx * _dir.x + dy * _dir.y + dz * _dir.z;
      if (along < MIN_ALONG || along > t.reach) continue;

      // Perpendicular distance from the ray, squared. Rearranged from
      // |v|² - (v·d)² so the square root is only taken for a candidate that
      // has already passed.
      const perp2 = Math.max(0, dx * dx + dy * dy + dz * dz - along * along);
      const allowed = t.radius + along * ANGLE_SLOP;
      if (perp2 > allowed * allowed) continue;

      let score = 1 - Math.sqrt(perp2) / allowed - along * DISTANCE_BIAS;
      // Compared against last frame's answer, which is what this still holds.
      if (t.id === this.selected) score += STICKY;
      if (score <= bestScore) continue;

      // Last, because it is the expensive test and most candidates have
      // already been rejected. The same rule the flat path applies: you cannot
      // take what you cannot see.
      if (grid) {
        _from.x = _origin.x;
        _from.z = _origin.z;
        _to.x = t.x;
        _to.z = t.z;
        if (!hasLineOfSight(grid, _from, _to)) continue;
      }
      best = i;
      bestScore = score;
    }
    this.pickScore = bestScore;
    return best;
  }

  /**
   * The mark on what is selected.
   *
   * A ring rather than a tint or an outline: it is one draw call, it costs no
   * shader recompile, it reads at six metres in a dark corridor, and it does
   * not need the thing it is marking to be a mesh this file knows anything
   * about. It is skipped for a person, who already has the strike ring.
   */
  private drawRing(dt: number, target: XRTarget | null, head: THREE.Camera): void {
    const group = this.group;
    const ring = this.ring;
    const material = this.material;
    if (!group || !ring || !material) return;

    if (this.flash > 0) this.flash = Math.max(0, this.flash - dt / FLASH_DECAY);
    const marking = target !== null && target.kind === 'thing';
    if (marking) {
      this.markX = target.x;
      this.markY = target.y;
      this.markZ = target.z;
    }
    this.fade = damp(this.fade, marking ? 1 : 0, RING_FADE_RATE, dt);

    // The flash outlives the target on purpose: the thing that was taken is
    // gone from the world the moment it is taken, and a mark that vanished with
    // it would leave the only confirmation of a successful pickup somewhere on
    // a HUD the player is not looking at.
    const strength = Math.max(this.fade, this.flash);
    if (strength < 0.01) {
      group.visible = false;
      return;
    }

    ring.position.set(this.markX, this.markY, this.markZ);
    const distance = ring.position.distanceTo(head.position);
    // Sized by angle rather than in metres, so it looks the same at two metres
    // and at six.
    let radius = clamp(distance * RING_ANGLE, RING_MIN, RING_MAX);
    radius += this.flash * 0.35;
    ring.scale.setScalar(radius);
    // Floor pickups sit at ankle height, so a ring big enough to see from
    // across the room would be mostly underneath the floor. Lifting its centre
    // keeps it a halo around the object instead of a stripe across it.
    ring.position.y = Math.max(this.markY, radius * 0.55);
    ring.quaternion.copy(head.quaternion);

    // A slow breath, so a live selection is distinguishable from a decal.
    const pulse = 0.86 + 0.14 * Math.sin(this.clock * 3.4);
    material.opacity = Math.min(1, strength * 0.5 * pulse + this.flash * 0.75);
    material.color.setHex(this.flash > 0 ? RING_FLASH_COLOUR : RING_COLOUR);
    group.visible = true;
  }
}

/**
 * Build the ray's interaction. Cheap and inert: nothing is added to the scene,
 * no geometry exists and no listener is attached until `attach()` is called,
 * which only ever happens when a session starts.
 */
export function makeXRInteract(host: XRInteractHost): XRInteract {
  return new Interact(host);
}
